import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { SKIP_OIDC_RELOGIN_ON_401 } from '../auth-routes';
import { OidcService } from '../oidc.service';

// Module-scoped state. Exposed (with the underscore prefix) so tests can reset it
// between cases — at runtime, the flag is naturally cleared on every full-page
// navigation to the IdP, which is the only path that sets it to true.
export const _oidcInvalidTokenInterceptorState = { redirectInProgress: false };

/**
 * Suffixes (matched against URL.pathname) for endpoints that must NOT trigger re-login on
 * 401. These are gateway-prefixed (`/authorization/...`) — the FE adds the prefix in
 * `endpoints.ts` before the request leaves the page.
 */
const SKIP_RELOGIN_PATH_SUFFIXES: readonly string[] = [
  '/authorization/oauth2/token',
  '/authorization/api/auth/login',
  '/api/exceptions',
];

function shouldSkipRelogin(reqUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(reqUrl, window.location.origin);
  } catch {
    return false;
  }
  for (let i = 0; i < SKIP_RELOGIN_PATH_SUFFIXES.length; i++) {
    const suffix = SKIP_RELOGIN_PATH_SUFFIXES[i];
    const path = parsed.pathname;
    if (path === suffix || path.length >= suffix.length && path.substring(path.length - suffix.length) === suffix) {
      return true;
    }
  }
  return false;
}

export const oidcInvalidTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const oidcService = inject(OidcService);

  if (shouldSkipRelogin(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !_oidcInvalidTokenInterceptorState.redirectInProgress) {
        // Caller opted out — request originated from a flow that must NOT re-arm OIDC
        // (e.g. LoginCallbackComponent's own user-fetch).
        if (req.context.get(SKIP_OIDC_RELOGIN_ON_401)) {
          return throwError(() => err);
        }
        _oidcInvalidTokenInterceptorState.redirectInProgress = true;
        const returnUrl = window.location.pathname + window.location.search;
        oidcService.login(returnUrl);
      }
      return throwError(() => err);
    })
  );
};
